/**
 * W-002 — admin topbar: environment badge, language switch, user menu.
 */
import { DownOutlined, GlobalOutlined, HomeOutlined, LogoutOutlined } from '@ant-design/icons'
import { useGetIdentity, useLogout, useSetLocale, useTranslate } from '@refinedev/core'
import { Avatar, Breadcrumb, Dropdown, Layout, Space, Tag, Typography } from 'antd'
import { useLocation } from 'react-router'
import { ENVIRONMENT } from '@/lib/supabase'
import type { AdminIdentity } from '@/providers/auth-provider'
import { useAdminLocale } from '@/providers/i18n-provider'
import type { Locale } from '@/types'

const ROLE_KEYS: Record<string, string> = {
  super_admin: 'admin.roles.superAdmin',
  content_manager: 'admin.roles.contentManager',
  moderator: 'admin.roles.moderator',
  support: 'admin.roles.support',
}

const pageLabelKey = (pathname: string) => {
  const labels: [string, string][] = [
    ['/content-items', 'content'],
    ['/subjects', 'subjects'],
    ['/ingest', 'ingest'],
    ['/users', 'users'],
    ['/moderation', 'moderation'],
    ['/community', 'posts'],
    ['/plans', 'plans'],
    ['/transactions', 'transactions'],
    ['/notifications', 'notifications'],
    ['/support', 'support'],
    ['/cms', 'cms'],
    ['/reference-data', 'referenceData'],
    ['/admin-users', 'administrators'],
    ['/audit-log', 'auditLog'],
  ]
  return labels.find(([prefix]) => pathname.startsWith(prefix))?.[1] ?? 'dashboard'
}

export const Header = () => {
  const { data: identity } = useGetIdentity<AdminIdentity>()
  const { mutate: logout } = useLogout()
  const locale = useAdminLocale()
  const changeLocale = useSetLocale()
  const translate = useTranslate()
  const location = useLocation()
  const currentPage = pageLabelKey(location.pathname)

  return (
    <Layout.Header
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
        background: 'transparent',
      }}
      className="admin-shell-header"
    >
      <Breadcrumb
        className="admin-header-context"
        items={[
          { title: <HomeOutlined /> },
          ...(currentPage !== 'dashboard'
            ? [{ title: translate(`admin.navigation.${currentPage}`) }]
            : []),
        ]}
      />

      <Space size="middle">
        {ENVIRONMENT !== 'production' ? (
          <Tag color="warning">{ENVIRONMENT.toUpperCase()}</Tag>
        ) : null}

        <Dropdown
          menu={{
            selectedKeys: locale ? [locale] : [],
            items: [
              { key: 'fr', label: translate('admin.language.french') },
              { key: 'ar', label: translate('admin.language.arabic') },
            ],
            onClick: ({ key }) => changeLocale(key as Locale),
          }}
        >
          <Space
            className="admin-locale-trigger"
            style={{ cursor: 'pointer' }}
            aria-label={translate('admin.language.switcher')}
          >
            <GlobalOutlined />
            {locale === 'ar'
              ? translate('admin.language.arabic')
              : translate('admin.language.french')}
          </Space>
        </Dropdown>

        <Dropdown
          menu={{
            items: [
              {
                key: 'logout',
                icon: <LogoutOutlined />,
                label: translate('admin.header.logout'),
                onClick: () => logout(),
              },
            ],
          }}
        >
          <Space className="admin-profile-trigger" style={{ cursor: 'pointer' }}>
            <Avatar size="small">{identity?.name?.charAt(0)?.toUpperCase() ?? '?'}</Avatar>
            <Space className="admin-profile-copy" direction="vertical" size={0}>
              <Typography.Text>{identity?.name}</Typography.Text>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {identity ? translate(ROLE_KEYS[identity.role] ?? identity.role) : ''}
              </Typography.Text>
            </Space>
            <DownOutlined />
          </Space>
        </Dropdown>
      </Space>
    </Layout.Header>
  )
}
